<template>
  <div v-if="product">
    <StepTitle :session="session" hr>
      <div v-if="showStepLabels" v-html="session.label"></div>
      <div v-else>{{title}}</div>
      <template #subtitle>
        <div v-if="showStepLabels" v-html="session.subtitle"></div>
        <div v-else>{{subtitle}}</div>
      </template>
      <template #product>
        <table class="selected-item-table selected-item">
          <tr>
            <td><img :src="getImageUrl" alt=""></td>
            <td class="product-name">{{quantity}} &times; <span v-html="product.name"></span></td>
          </tr>
        </table>
      </template>
    </StepTitle>
    <VariantQuantities
      v-if="useQuantitySelections"
      :session="session"
      :selected-quantities="selectedQuantities"
      :total-quantity="expectedQuantity"
      @select="selectQuantities"
      @back="goBack"
    ></VariantQuantities>
    <template v-else>
      <div>
        <section class="boxes select-item-table">
          <div class="index-content centered-block ">
            <b-row class="justify-content-md-center boxes select-item-btn customer-verify variant-selector-container">
              <b-col lg="12" cols="12">
                <div class="variant-selectors" v-for="index in expectedQuantity" :key="index">
                  <div class="bundle-index" v-if="expectedQuantity > 1">
                    <span class="bundle-index-text">Bundle Item {{index}}</span>
                  </div>
                  <div class="variant-group" v-for="name of Object.keys(variants)" :key="'row-' + name + '-' + index">
                    <label :for="'id-' + name + '-' + index">
                      {{name.toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))}}
                      <span v-if="expectedQuantity > 1"> #{{index}}</span>
                    </label>
                    <select :id="'id-' + name + '-' + index" class="variant-dropdown"
                            v-model="selections[name][index-1]" required>
                      <option :value="null">-- Please Select --</option>
                      <option v-for="value in variants[name]" :key="'variant-' + index + '-' + name + '-' + value" :value="value">{{value}}</option>
                    </select>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </section>
      </div>
      <b-row v-if="showStepLabels" class="justify-content-md-center boxes claim mt-4 variant-btns-wrapper" :class="{'new-btns-container': !session.is_legacy}">
        <b-col md="7" cols="12" :class="{'my-class': !session.is_legacy}">
          <b-row class="choice-fields-btns">
            <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" cols="12" class="text-center">
              <button @click="done()"
                      class="btn-base input-choice-btn input-btn"
                      :style="getStyles(input)">
                <div v-html="sessionInputLabel(input)"></div>
              </button>
            </b-col>
            <b-col v-if="showBackButton" cols="12" class="back">
              <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row v-else class="justify-content-center boxes select-item-btn customer-verify mt-4 variant-container-btns">
        <b-col v-if="showBackButton" md="4" cols="6" class="back">
          <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
        </b-col>
        <b-col :md="showBackButton ? 4 : 8" :cols="showBackButton ? 6 : 12">
          <b-button block @click="done()" class="btn btn-default btn-account font-weight-normal select-item-continue-btn" variant="primary">Continue
            <font-awesome-icon :icon="['fas', 'caret-right']" class="footer_btn"></font-awesome-icon>
          </b-button>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import {getDefaultProductImageUrl, updateFontSizes} from '@/utils'
import {StepCategory, VariantType} from '@/constants'

const StepTitle = () => import('./StepTitle')
const VariantQuantities = () => import('./VariantQuantities')

export default {
  name: 'Variants',
  components: {StepTitle, VariantQuantities},
  props: {
    session: Object,
    item: Object,
    newCampaignProduct: Object,
    subscriptionOnly: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    quantity: {
      type: Number,
      default: function() {
        return 1
      }
    }
  },
  data: function() {
    return {
      variants: {},
      selections: {},
      idMap: {},
      selectedQuantities: {},
      confirmSelection: false,
      product: null,
      bundleQuantity: 1
    }
  },
  computed: {
    expectedQuantity: function() {
      return this.isBundledVariant ? this.quantity * this.bundleQuantity : this.quantity
    },
    showBackButton: function() {
      return this.session && (this.session.can_undo || this.session.is_step_preview)
    },
    getImageUrl: function() {
      return getDefaultProductImageUrl(this.product, 'small')
    },
    title: function() {
      return this.confirmSelection ? 'Confirm Your Selection' : 'Choose Your Options'
    },
    subtitle: function() {
      return this.useQuantitySelections ? 'Total quantity of selections must equal ' + this.expectedQuantity.toString()
        : ''
    },
    isBundledVariant: function() {
      return this.product.variant_type === VariantType.Bundled
    },
    useQuantitySelections: function() {
      if (this.expectedQuantity > 1) {
        const totalChoices = Object.keys(this.variants).length * this.expectedQuantity
        return totalChoices > 10
      }
      return false
    },
    showStepLabels: function() {
      return this.session && this.session.step_category === StepCategory.ChangeVariant
    }
  },
  methods: {
    selectQuantities: function(selectedQuantities) {
      this.selectedQuantities = selectedQuantities
      let selections = []

      for (const [id, row] of Object.entries(selectedQuantities)) {
        const quantity = parseInt(row.quantity)

        if (!isNaN(quantity) && (quantity > 0)) {
          selections.push({id: id, quantity: quantity})
        }
      }

      this.$emit('select', {variants: selections})
    },
    done: function() {
      let quantities = {}
      this.confirmSelection = false
      this.$store.commit('clear_messages')
      const names = Object.keys(this.selections).sort()

      for (let i = 0; i < this.expectedQuantity; i++) {
        let pairs = []

        for (let optionName of names) {
          const value = this.selections[optionName][i]

          if (!value) {
            this.$store.commit('error', 'Please fill out all fields')
            return
          }

          pairs.push(optionName + '=' + value)
        }

        const id = this.idMap[pairs.join(',')]

        if (id in quantities) {
          quantities[id] += 1
        } else {
          quantities[id] = 1
        }
      }

      // done selecting variants
      let selections = []

      for (const [id, quantity] of Object.entries(quantities)) {
        selections.push({id: id, quantity: quantity})
      }

      this.$emit('select', {variants: selections})
    },
    goBack: function() {
      this.$emit('back') // go back to the previous step (ignore selections)
    },
    getStyles: function(input) {
      return input && input.button_styles && input.button_styles.style
    },
    sessionInputLabel(input) {
      if (input) {
        return updateFontSizes(input.formatted_label)
      }
      return null
    }
  },
  created() {
    this.$store.commit('busy')
    let product = this.item.product
    let selectedVariant = this.item.variant
    let bundleQuantity = this.item.campaign_product.bundle_quantity
    let bundledVariants = this.item.bundled_variants

    if (this.newCampaignProduct) {
      product = this.newCampaignProduct.product
      bundleQuantity = this.newCampaignProduct.bundle_quantity
      selectedVariant = null
      bundledVariants = []
    } else if (this.isSubscriptionOnly) {
      product = this.item.next_cycle_product
      bundleQuantity = this.item.next_bundle_quantity
      selectedVariant = this.item.next_variant
      bundledVariants = this.item.next_bundled_variants
    }

    axios.get('/products/' + product.id.toString() + '/variants/').then(response => {
      // build unique set of variant options and map them to their ids
      console.log(response)
      const variants = response.data
      this.product = product
      this.bundleQuantity = bundleQuantity
      let variantOptionMap = {}
      let variantOptions = {}
      let selections = {}
      let idMap = {}
      let selectedQuantities = {}

      variants.forEach(variant => {
        let pairs = []
        let options = {}

        variant.options.forEach(option => {
          if (!(option.name in variantOptionMap)) {
            variantOptionMap[option.name] = {}
          }
          variantOptionMap[option.name][option.value] = true

          pairs.push(option.name + '=' + option.value)
          options[option.name] = option.value
        })

        const key = pairs.sort().join(',')
        idMap[key] = variant.id
        selectedQuantities[variant.id] = {quantity: 0, options: options, key: key}
      })

      // convert variants to sorted lists and initialize selections by index
      Object.keys(variantOptionMap).forEach(optionName => {
        variantOptions[optionName] = Object.keys(variantOptionMap[optionName]).sort()
        selections[optionName] = []

        for (let i = 0; i < this.expectedQuantity; i++) {
          selections[optionName].push(null)
        }
      })

      // get initial selected variants
      if (this.isBundledVariant) {
        if (bundledVariants && bundledVariants.length) {
          let startIndex = 0

          bundledVariants.forEach(bundledVariant => {
            selectedQuantities[bundledVariant.variant.id].quantity = bundledVariant.quantity

            for (let i = 0; (i < bundledVariant.quantity) && ((startIndex + i) < this.expectedQuantity); i++) {
              for (const option of bundledVariant.variant.options) {
                selections[option.name][startIndex + i] = option.value
              }
              startIndex += bundledVariant.quantity
            }
          })
        }
      } else {
        if (selectedVariant && selectedVariant.options) {
          selectedQuantities[selectedVariant.id].quantity = this.quantity

          for (let i = 0; i < this.quantity; i++) {
            for (const option of selectedVariant.options) {
              selections[option.name][i] = option.value
            }
          }
        }
      }

      this.variants = variantOptions
      this.idMap = idMap
      this.selectedQuantities = selectedQuantities
      this.selections = selections
      this.$store.commit('ready')
    }, error => {
      console.error(error)
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
    })
  }
}
</script>

<style scoped lang="less">
.stroke{
  border: 2px solid;
}

.bundle-index {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.bundle-index-text {
  font-size: 18px;
  font-weight: bold;
  padding-bottom: 5px;
  border-bottom: 2px solid var(--theme_color);
}

.variant-selectors {
  width: 80%;
  justify-self: center;
}

.variant-btns-wrapper {
  .choice-fields-btns {
    margin: 0 auto;
  }

  button,
  .btn-back {
    padding: 12px 16px !important;
    min-height: 44px !important;
    text-transform: none;
  }
  .btn-back {
    padding: 8px 16px !important;
  }
}

@media screen and (max-width: 767px) {
  .variant-selectors {
    width: 100%;
  }
}

</style>
